import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SearchBar from './components/SearchBar';
import NewsList from './components/NewsList';
import MetaData from './components/MetaData';
import Pagination from "./components/Pagination";
import './styles/App.css';
import logo from './images/logo.news-index.png';
import loadingGif from './images/loading.gif';
import useDebounce from './hooks/useDebounce';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment-timezone";


const App = () => {
    const [meta, setMeta] = useState([]);

    const [news, setNews] = useState([]);
    const [keyword, setKeyword] = useState('');
    const [loading, setLoading] = useState(false);

    const [sentiments, setSentiments] = useState({
        positive: false,
        negative: false,
        neutral: false,
    });
    const handleSentimentChange = (e) => {
        const { name, checked } = e.target;
        setSentiments(prevSentiments => ({
            ...prevSentiments,
            [name]: checked,
        }));
    };


    const [sources, setSources] = useState({
        "nytimes": false,
        "foxnews": false,
        "aljazeera": false,
        "thedailystar": false,
        "dw": false
    });
    const handleSourceChange = (e) => {
        const { name, checked } = e.target;
        setSources(prevSources => ({
            ...prevSources,
            [name]: checked,
        }));
    };


    // Helper functions to get the start and end of the current day
    const getStartOfDay = () => {
        const specificDate = new Date();
        specificDate.setFullYear(2024, 6, 15); // Sets the year, month (0-based index), and day
        specificDate.setHours(0, 0, 0, 0); // Sets the time to midnight
        return specificDate;
    };

    const getEndOfDay = () => {
        const now = new Date();
        now.setHours(23, 59, 59, 999);
        return now;
    };

    // Initialize state with the start and end of the current day
    const [startDate, setStartDate] = useState(getStartOfDay());
    const [endDate, setEndDate] = useState(getEndOfDay());

    // Get the user's timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;



    const [sortBy, setSortBy] = useState('pub_date_timestamp');
    const [sortOrder, setSortOrder] = useState('desc');

    const handleSortByChange = (e) => {
        setSortBy(e.target.value);
        console.log('Sort By changed:', e.target.value); // Debugging line
        // Handle sorting logic here
    };

    const handleSortOrderChange = (e) => {
        setSortOrder(e.target.value);
        console.log('Sort Order changed:', e.target.value); // Debugging line

        // Handle sorting logic here
    };



    // Debounce or Delay
    const debouncedKeyword = useDebounce(keyword, 300);


    // Call API to Fetch News
    const fetchNews = useCallback(async (searchKeyword) => {
        setLoading(true);

        let url = `${process.env.REACT_APP_API_URL}/search`;

        // Append keyword if available
        if (searchKeyword) {
            url += `?keyword=${searchKeyword}`;
        }
        // Append sources if they are selected
        const selectedSource = Object.keys(sources)
            .filter(source => sources[source])
            .join(',');
        if (selectedSource) {
            url += `${searchKeyword ? '&' : '?'}source=${selectedSource}`;
        }
        // Append sentiments if they are selected
        const selectedSentiments = Object.keys(sentiments)
            .filter(sentiment => sentiments[sentiment])
            .join(',');
        if (selectedSentiments) {
            url += `${searchKeyword || selectedSource ? '&' : '?'}sentiment=${selectedSentiments}`;
        }

        // Append startDate and endDate
        if (startDate & endDate) {
            let newStartDate = `${moment(startDate).tz("utc").unix()}`;
            let newEndDate = `${moment(endDate).tz("utc").unix()}`;
            url += `${searchKeyword || selectedSource || selectedSentiments ? '&' : '?'}startDate=${newStartDate}&endDate=${newEndDate}`;
        }

        // Append sortBy and sortOrder
        if (sortBy) {
            url += `${searchKeyword || selectedSource || selectedSentiments || startDate || endDate ? '&' : '?'}sortBy=${sortBy}`;
        }
        if (sortOrder) {
            url += `${searchKeyword || selectedSource || selectedSentiments || startDate || endDate ? '&' : '?'}sortOrder=${sortOrder}`;
        }

        console.log(url);


        try {
            const response = await axios.get(url);
            if (response.data.status === 'success') {
                setMeta(response.data.meta);
                setNews(response.data.data);
            } else {
                setNews([]);
            }
        } catch (error) {
            console.error('Error fetching news:', error);
            setNews([]);
        } finally {
            setLoading(false);
        }
    }, [sources, sentiments, startDate, endDate, sortBy, sortOrder]);


    // Effect
    useEffect(() => {
        fetchNews(debouncedKeyword);
    }, [debouncedKeyword, fetchNews, sources, sentiments, startDate, endDate, sortBy, sortOrder]);


    // Search
    const handleSearch = (newKeyword) => {
        setKeyword(newKeyword);
    };

    // Pagination
    const handlePageChange = (url) => {
        if (url) {
            let new_url = url.replace('/search?keyword=', '');
            fetchNews(new_url);
        }
    };




    return (
        <div className="App">
            <div className='LogoHead'>
                <h1>
                    <img src={logo} alt="NewsIndex Logo" id="nilogo" />
                    <a href="/">NewsIndex</a>
                    <span className="beta-label">(beta)</span>
                </h1>
                <h2>Sentiment Analyzed NewsIndex</h2>
            </div>

            <SearchBar onSearch={handleSearch} />




            {loading ? (
                <div className="Loading">
                    <img src={loadingGif} alt="Loading..." />
                </div>
            ) : (
                news && news.length > 0 ? (
                    <Pagination meta={meta} onPageChange={handlePageChange} />
                ) : (
                    <div></div>
                )
            )}


            <div className='theContents'>
                <div className='theContentList'>

                    {loading ? (
                        <div className="Loading">
                            <img src={loadingGif} alt="Loading..." />
                        </div>
                    ) : (
                        news && news.length > 0 ? (

                            <NewsList news={news} />
                        ) : (
                            <div className='notAvailable'>"No news available."</div>
                        )
                    )}

                </div>
                <div className='sideBar'>

                    <div className="filterAndSort">
                        <h2 className='sideBarHeading'>Sort & Filter</h2>                                

                        {loading ? (
                        <div className="Loading">
                            <img src={loadingGif} alt="Loading..." />
                        </div>
                        ) : (
                            <div className="SortSection">
                            <div className="SortBy">
                                <label htmlFor="sort_by">Sort By:</label>
                                <select id="sort_by" value={sortBy} onChange={handleSortByChange}>
                                    <option value="pub_date_timestamp">Publication Date</option>
                                    <option value="title">Title</option>
                                    <option value="sentiment">Sentiment</option>
                                    <option value="description">Description</option>
                                </select>
                            </div>
                            <div className="SortOrder">
                                <label htmlFor="SortOrder">Sort Order:</label>
                                <select id="SortOrder" value={sortOrder} onChange={handleSortOrderChange}>
                                    <option value="asc">Ascending</option>
                                    <option value="desc">Descending</option>
                                </select>
                            </div>
                        </div>
                    )}





                        {loading ? (
                            <div className="Loading">
                                <img src={loadingGif} alt="Loading..." />
                            </div>
                        ) : (
                            <div className="SourceOptions">
                                <label className='filterCatLabel'>Filter by Source: </label>
                                <label className='filterOptionsLabel'>
                                    <input
                                        type="checkbox"
                                        name="thedailystar"
                                        checked={sources.thedailystar}
                                        onChange={handleSourceChange}
                                    /> TheDailyStar
                                </label>
                                <label className='filterOptionsLabel'>
                                    <input
                                        type="checkbox"
                                        name="foxnews"
                                        checked={sources.foxnews}
                                        onChange={handleSourceChange}
                                    /> FoxNews
                                </label>
                                <label className='filterOptionsLabel'>
                                    <input
                                        type="checkbox"
                                        name="nytimes"
                                        checked={sources.nytimes}
                                        onChange={handleSourceChange}
                                    /> NewYorkTimes
                                </label>
                                <label className='filterOptionsLabel'>
                                    <input
                                        type="checkbox"
                                        name="aljazeera"
                                        checked={sources.aljazeera}
                                        onChange={handleSourceChange}
                                    /> Aljazeera
                                    </label>
                                    <label className='filterOptionsLabel'>
                                    <input
                                        type="checkbox"
                                        name="dw"
                                        checked={sources.dw}
                                        onChange={handleSourceChange}
                                    /> DW
                                </label>
                            </div>
                        )}


                        {loading ? (
                            <div className="Loading">
                                <img src={loadingGif} alt="Loading..." />
                            </div>
                        ) : (
                            <div className="SentimentOptions">
                                <label className='filterCatLabel'>Filter by Sentiment: </label>
                                <label className='filterOptionsLabel'>
                                    <input
                                        type="checkbox"
                                        name="positive"
                                        checked={sentiments.positive}
                                        onChange={handleSentimentChange}
                                    /> Positive
                                </label>
                                <label className='filterOptionsLabel'>
                                    <input
                                        type="checkbox"
                                        name="negative"
                                        checked={sentiments.negative}
                                        onChange={handleSentimentChange}
                                    /> Negative
                                </label>
                                <label className='filterOptionsLabel'>
                                    <input
                                        type="checkbox"
                                        name="neutral"
                                        checked={sentiments.neutral}
                                        onChange={handleSentimentChange}
                                    /> Neutral
                                </label>
                            </div>
                        )}

                        {loading ? (
                            <div className="Loading">
                                <img src={loadingGif} alt="Loading..." />
                            </div>
                        ) : (
                            <div className='datetimefilter'>
                                <label className='filterCatLabel'>Filter by DateTime: </label>
                                {/* Include the DateTimeRangePicker component */}
                                {/* <DateTimeRangePicker onDateChange={(start, end) => fetchNews(null, start, end)} /> */}
                                <br />
                                <label htmlFor="startDate">Start DateTime</label>
                                <DatePicker
                                    id="startDate"
                                    selected={startDate}
                                    onChange={(date) => setStartDate(date)}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    showTimeSelect
                                    timeIntervals={15}
                                />
                                <br />
                                <label htmlFor="endDate">End DateTime</label>
                                <DatePicker
                                    id="endDate"
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    showTimeSelect
                                    timeIntervals={15}
                                    minDate={startDate}
                                />

                                <p><strong>Selected StartDate:</strong> {moment(startDate).tz(userTimezone).format('MMMM Do YYYY, h:mm:ss a')} ({userTimezone})</p>
                                <p><strong>Selected EndDate:</strong> {moment(endDate).tz(userTimezone).format('MMMM Do YYYY, h:mm:ss a')} ({userTimezone})</p>

                            </div>
                        )}


                    </div>

                    {loading ? (
                        <div className="Loading">
                            <img src={loadingGif} alt="Loading..." />
                        </div>
                    ) : (
                        news && news.length > 0 ? (<MetaData meta={meta} />) : (<div></div>)
                    )}


                    <div className='intro'>
                        <h2 className='sideBarHeading'>About</h2>
                        <p>News from different sources is listed here. All the content is solely owned by the news sites.
                            There are three colors symbolizing the sentiment of the news
                            content: <span className="positive">Positive</span> as Turquoise Green, <span className="negative">Negative</span> as Coral Red, and <span className="neutral">Neutral</span> as Sunflower Yellow.
                        </p>
                        <p>Sentiment analysis done by some open-source Sentiment Analysis Tools. Also expect your input about the content sentiment to make it more correct.</p>

                    </div>


                    <div className='copyright'>
        <p>&copy; 2024 <a href='/'>NewsIndex</a>. All Rights Reserved.</p>
        <p>All content, including text, images, and links to external sites, is provided for informational purposes only. 
        Any external content linked to or referenced on this site remains the property of the respective content owners.</p>
        <p>Unauthorized reproduction or redistribution of this material is prohibited. For permissions or inquiries, please contact us.</p>
    </div>


                </div>
            </div>

            {loading ? (
                <div className="Loading">
                    <img src={loadingGif} alt="Loading..." />
                </div>
            ) : (
                news && news.length > 0 ? (
                    <Pagination meta={meta} onPageChange={handlePageChange} />
                ) : (
                    <div></div>
                )
            )}


        </div>
    );
};

export default App;
